import { Component, forwardRef, Input, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { NG_VALUE_ACCESSOR, UntypedFormArray, UntypedFormBuilder } from '@angular/forms'
import { AllRuleControlComponentBase } from '../all-rule-control-base'
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop'
import { Observable } from 'rxjs'
import { map, startWith } from 'rxjs/operators'
import { RuleCount } from '../rule-count.service'
import { SelectOption } from '../custom-rule-dropdown/custom-rule-dropdown.component'
import { ApiService } from '../../services/api.service'
import { UiService } from '../../services/ui.service'
import { ApplicationType } from '../../services/dtos'

@Component({
  selector: 'app-resource-rule-selector',
  templateUrl: './app-resource-rule-selector.component.html',
  styleUrls: ['./app-resource-rule-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppResourceRuleSelectorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppResourceRuleSelectorComponent extends AllRuleControlComponentBase implements OnInit {
  // possibleTags is used for auto-complete if desired.  It contains
  // a map of suggested tag names to the set of
  // suggested tag values for that tag name.
  // null = not yet loaded, empty Map = loaded with no tags
  @Input() possibleTags: Map<string, Set<string>> | null = null
  @Input() readonly: boolean = false

  // True only while source resources are still loading; once loaded (even with no tags), this is false
  get isTagsLoading(): boolean {
    return this.possibleTags === null
  }

  // possibleResourceTypes contains available resource types for the dropdown
  @Input() possibleResourceTypes: Array<{ value: string; displayName: string }> | null = []

  // possibleResourceGroups contains available resource groups for the dropdown
  @Input() possibleResourceGroups: Array<{ value: string; displayName: string }> | null = []

  // applicationType determines which resource types to show
  @Input() applicationType: ApplicationType

  // action determines if this is for inclusion or exclusion rules
  @Input() action: 'include' | 'exclude' = 'include'

  // Converted options for custom selects - dynamically built to include resource groups only when available
  get filterTypeOptions(): SelectOption[] {
    const options: SelectOption[] = [
      { value: 'tag', label: 'Tag' },
      { value: 'resourceType', label: 'Resource Type' },
    ]

    // Show Resource Group option for Azure applications
    if (this.applicationType === ApplicationType.AZURE) {
      options.push({ value: 'resourceGroup', label: 'Resource Group' })
    }

    return options
  }

  operatorOptions: SelectOption[] = [
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' },
  ]

  constructor(
    protected formBuilder: UntypedFormBuilder,
    protected uiService: UiService,
    private apiService: ApiService
  ) {
    super(formBuilder, uiService)
  }

  showTagSelect(ruleControl: any): boolean {
    return (
      ruleControl.get('filterType')?.value !== 'resourceType' &&
      ruleControl.get('filterType')?.value !== 'resourceGroup'
    )
  }

  showResourceTypeSelect(ruleControl: any): boolean {
    return ruleControl.get('filterType')?.value === 'resourceType'
  }

  showResourceGroupSelect(ruleControl: any): boolean {
    return ruleControl.get('filterType')?.value === 'resourceGroup'
  }

  // Convert possibleTags Map to SelectOption array for tag names
  get tagNameOptions(): SelectOption[] {
    if (!this.possibleTags) return []
    const options: SelectOption[] = []
    Array.from(this.possibleTags.keys()).forEach((tagName) => {
      options.push({ value: tagName, label: tagName })
    })
    return options
  }

  // Get tag value options for a specific tag name
  getTagValueOptions(tagName: string): SelectOption[] {
    const options: SelectOption[] = [{ value: '', label: 'Any value' }]
    const values = this.possibleTags?.get(tagName) || new Set()
    Array.from(values).forEach((value) => {
      options.push({ value, label: value })
    })
    return options
  }

  // Convert possibleResourceTypes to SelectOption array
  get resourceTypeOptions(): SelectOption[] {
    const options: SelectOption[] = []
    if (this.possibleResourceTypes) {
      this.possibleResourceTypes.forEach((resourceType) => {
        options.push({
          value: resourceType.value,
          label: resourceType.displayName,
        })
      })
    }
    return options
  }

  // Convert possibleResourceGroups to SelectOption array
  get resourceGroupOptions(): SelectOption[] {
    const options: SelectOption[] = []
    if (this.possibleResourceGroups) {
      this.possibleResourceGroups.forEach((resourceGroup) => {
        options.push({
          value: resourceGroup.value,
          label: resourceGroup.displayName,
        })
      })
    }
    return options
  }

  // Generate storage keys for custom selects
  getStorageKey(type: string): string {
    return `${this.action}-${type}`
  }

  // Check if operator should be disabled in AND groups
  isOperatorDisabled(option: SelectOption, groupIndex: number, ruleIndex: number): boolean {
    if (option.value === 'is_not') {
      return !this.canUseIsNotInAndGroup(groupIndex, ruleIndex)
    }
    return false
  }

  // Get operator options with disabled state for a specific rule
  getOperatorOptions(groupIndex: number, ruleIndex: number): SelectOption[] {
    return this.operatorOptions.map((opt) => ({
      ...opt,
      disabled: this.isOperatorDisabled(opt, groupIndex, ruleIndex),
    }))
  }

  // Handle when new options are added to tag names
  onTagNameOptionAdded(newOption: SelectOption): void {
    if (this.possibleTags && !this.possibleTags.has(newOption.value)) {
      this.possibleTags.set(newOption.value, new Set())
    }
  }

  // Handle when new options are added to tag values
  onTagValueOptionAdded(newOption: SelectOption, tagName: string): void {
    if (tagName && this.possibleTags?.has(tagName)) {
      const tagValues = this.possibleTags.get(tagName)!
      tagValues.add(newOption.value)
    }
  }

  // Handle when tag name options are removed
  onTagNameOptionRemoved(removedOption: SelectOption): void {
    this.possibleTags?.delete(removedOption.value)
  }

  // Handle when tag value options are removed
  onTagValueOptionRemoved(removedOption: SelectOption, tagName: string): void {
    if (tagName && this.possibleTags?.has(tagName)) {
      const tagValues = this.possibleTags.get(tagName)!
      tagValues.delete(removedOption.value)
    }
  }

  // TrackBy function for resource types to optimize Angular change detection
  resourceTypeTrackBy(_index: number, resourceType: { value: string; displayName: string }): string {
    return resourceType.value
  }

  // Handle tag name changes to enable/disable tag value dropdown
  onTagNameChange(ruleControl: any): void {
    const tagValueControl = ruleControl.get('tagValue')
    const tagNameValue = ruleControl.get('tagName')?.value

    if (tagNameValue && tagNameValue.length > 0) {
      tagValueControl?.enable()
    } else {
      tagValueControl?.disable()
      tagValueControl?.setValue('')
    }
  }

  // Handle filterType changes to reset form controls
  onFilterTypeChange(ruleControl: any): void {
    const filterType = ruleControl.get('filterType')?.value

    // Only clear controls when switching TO tag mode
    if (filterType === 'tag') {
      // Clear tag-related controls
      ruleControl.get('tagName')?.setValue('')
      ruleControl.get('tagValue')?.setValue('')
    }
    // Don't clear resourceType or resourceGroup when switching to those modes
  }

  dropGroup(event: CdkDragDrop<any[]>) {
    const formArray = this.getFormArray(this.ruleForm.get('ruleFormArray'))
    moveItemInArray(formArray.controls, event.previousIndex, event.currentIndex)
    formArray.updateValueAndValidity()

    // Fix any invalid "is_not" operators after drag
    this.validateAndFixOperators()
  }

  // Override the getAction method to return the current action
  protected getAction(): string {
    return this.action
  }

  // Get the raw form row count that matches what users see in the UI
  getFormRowCount$(): Observable<RuleCount> {
    const formArray = this.ruleForm.get('ruleFormArray') as UntypedFormArray
    return formArray.valueChanges.pipe(
      startWith(formArray.value),
      map((formValues) => {
        if (!formValues || !Array.isArray(formValues)) {
          return { total: 0, inner: 0 }
        }

        let totalRules = 0
        let innerRules = 0

        formValues.forEach((formRowValue: any) => {
          if (formRowValue && formRowValue.type === 'all' && formRowValue.rules && Array.isArray(formRowValue.rules)) {
            // This is an AND group - only count non-empty inner rules
            const validInnerRules = formRowValue.rules.filter((innerRule: any) => {
              return (
                innerRule &&
                ((innerRule.tagName && innerRule.tagName.length > 0) ||
                  (innerRule.resourceType && innerRule.resourceType.length > 0) ||
                  (innerRule.resourceGroup && innerRule.resourceGroup.length > 0))
              )
            })
            if (validInnerRules.length > 0) {
              totalRules += validInnerRules.length // Count each valid inner rule separately
            }
          } else {
            // This is an OR rule - only count if it has content
            const hasTagContent = formRowValue && formRowValue.tagName && formRowValue.tagName.length > 0
            const hasResourceTypeContent =
              formRowValue && formRowValue.resourceType && formRowValue.resourceType.length > 0
            const hasResourceGroupContent =
              formRowValue && formRowValue.resourceGroup && formRowValue.resourceGroup.length > 0
            if (hasTagContent || hasResourceTypeContent || hasResourceGroupContent) {
              totalRules += 1
            }
          }
        })

        return { total: totalRules, inner: innerRules }
      })
    )
  }
}
